import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { Bell, Mail, MessageSquare, Play, Trash2 } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API_BASE } from "@/config/api"
import { formatDate } from "@/lib/format"
import { toast } from "sonner"

const API = API_BASE

const HOUR_PRESETS = [2, 24, 48, 72]

function channelLabel(channel, t) {
  if (channel === "sms") return t("app.reminderRules.channelSms")
  return t("app.reminderRules.channelEmail")
}

export default function ReminderRules() {
  const { t } = useTranslation()
  const [rules, setRules] = useState([])
  const [channels, setChannels] = useState({ email: false, sms: false })
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [form, setForm] = useState({ channel: "email", hours_before: 24 })
  const [editingId, setEditingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [rulesRes, histRes] = await Promise.all([
        axios.get(`${API}/reminder-rules`),
        axios.get(`${API}/appointment-reminders`, { params: { limit: 30 } }),
      ])
      const payload = rulesRes.data
      if (Array.isArray(payload)) {
        setRules(payload)
        setChannels({ email: true, sms: false })
      } else {
        setRules(payload.rules || [])
        setChannels(payload.channels || { email: false, sms: false })
      }
      setHistory(histRes.data || [])
    } catch {
      toast.error(t("app.reminderRules.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  const resetForm = () => {
    setForm({ channel: "email", hours_before: 24 })
    setEditingId(null)
  }

  const saveRule = async () => {
    const hours = Number(form.hours_before)
    if (!hours || hours < 1 || hours > 168) {
      return toast.error(t("app.reminderRules.toast.invalidHours"))
    }
    try {
      await axios.post(`${API}/reminder-rules`, {
        id: editingId || undefined,
        channel: form.channel,
        hours_before: hours,
        enabled: true,
      })
      resetForm()
      load()
      toast.success(editingId ? t("app.reminderRules.toast.updated") : t("app.reminderRules.toast.added"))
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.reminderRules.toast.saveFailed"))
    }
  }

  const toggleRule = async (rule) => {
    try {
      await axios.post(`${API}/reminder-rules`, {
        id: rule.id,
        channel: rule.channel,
        hours_before: rule.hours_before,
        enabled: !rule.enabled,
      })
      load()
    } catch {
      toast.error(t("app.reminderRules.toast.saveFailed"))
    }
  }

  const deleteRule = async (id) => {
    try {
      await axios.delete(`${API}/reminder-rules/${id}`)
      if (editingId === id) resetForm()
      load()
      toast.success(t("app.reminderRules.toast.deleted"))
    } catch {
      toast.error(t("app.reminderRules.toast.saveFailed"))
    }
  }

  const startEdit = (rule) => {
    setEditingId(rule.id)
    setForm({ channel: rule.channel || "email", hours_before: rule.hours_before })
  }

  const runNow = async () => {
    setRunning(true)
    try {
      const { data } = await axios.post(`${API}/reminders/run`)
      toast.success(
        t("app.reminderRules.toast.runResult", {
          email: data.sent_email || 0,
          sms: data.sent_sms || 0,
          total: (data.sent_email || 0) + (data.sent_sms || 0),
        }),
      )
      load()
    } catch {
      toast.error(t("app.reminderRules.toast.runFailed"))
    } finally {
      setRunning(false)
    }
  }

  const seedDefaults = async () => {
    const defaults = [
      { channel: "email", hours_before: 48 },
      { channel: "sms", hours_before: 48 },
      { channel: "email", hours_before: 2 },
      { channel: "sms", hours_before: 2 },
    ]
    try {
      for (const d of defaults) {
        await axios.post(`${API}/reminder-rules`, { ...d, enabled: true })
      }
      load()
      toast.success(t("app.reminderRules.toast.defaultsAdded"))
    } catch {
      toast.error(t("app.reminderRules.toast.saveFailed"))
    }
  }

  return (
    <FeatureGate feature="appointments">
      <AppLayout title={t("app.nav.reminderRules")}>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.reminderRules.intro")}{" "}
          <Link to="/app/communications" className="underline underline-offset-2">
            {t("app.nav.communications")}
          </Link>
          {" · "}
          <Link to="/app/settings" className="underline underline-offset-2">
            {t("app.nav.settings")}
          </Link>
        </p>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          <div className="space-y-6">
            <section className="rounded-xl border border-border p-4 bg-card space-y-3">
              <h3 className="text-sm font-medium">{t("app.reminderRules.channelsTitle")}</h3>
              <div className="flex flex-wrap gap-2">
                <Badge variant={channels.email ? "default" : "secondary"} className="gap-1">
                  <Mail className="h-3 w-3" />
                  Email — {channels.email ? t("app.reminderRules.configured") : t("app.reminderRules.notConfigured")}
                </Badge>
                <Badge variant={channels.sms ? "default" : "secondary"} className="gap-1">
                  <MessageSquare className="h-3 w-3" />
                  SMS — {channels.sms ? t("app.reminderRules.configured") : t("app.reminderRules.notConfigured")}
                </Badge>
              </div>
              {(!channels.email || !channels.sms) && (
                <PilotBanner>{t("app.reminderRules.channelsPilot")}</PilotBanner>
              )}
            </section>

            <section className="rounded-xl border border-border p-6 bg-card space-y-4">
              <h3 className="text-sm font-medium">
                {editingId ? t("app.reminderRules.editRule") : t("app.reminderRules.addRule")}
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("app.reminderRules.fieldChannel")}</Label>
                  <Select
                    value={form.channel}
                    onValueChange={(v) => setForm((f) => ({ ...f, channel: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">{t("app.reminderRules.channelEmail")}</SelectItem>
                      <SelectItem value="sms">{t("app.reminderRules.channelSms")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("app.reminderRules.fieldHours")}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    className="mt-1"
                    value={form.hours_before}
                    onChange={(e) => setForm((f) => ({ ...f, hours_before: Number(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {HOUR_PRESETS.map((h) => (
                  <Button
                    key={h}
                    type="button"
                    size="sm"
                    variant={form.hours_before === h ? "default" : "outline"}
                    className="rounded-full h-8"
                    onClick={() => setForm((f) => ({ ...f, hours_before: h }))}
                  >
                    {t("app.reminderRules.hoursPreset", { hours: h })}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full" onClick={saveRule}>
                  {editingId ? t("app.reminderRules.saveChanges") : t("app.reminderRules.addRuleBtn")}
                </Button>
                {editingId && (
                  <Button variant="ghost" className="rounded-full" onClick={resetForm}>
                    {t("app.reminderRules.cancelEdit")}
                  </Button>
                )}
              </div>
            </section>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="rounded-full gap-2"
                onClick={runNow}
                disabled={running}
              >
                <Play className="h-4 w-4" />
                {t("app.reminderRules.runNow")}
              </Button>
              {!rules.length && !loading && (
                <Button variant="ghost" className="rounded-full" onClick={seedDefaults}>
                  {t("app.reminderRules.useDefaults")}
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
              {loading && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.reminderRules.loading")}</li>
              )}
              {!loading &&
                rules.map((r) => (
                  <li key={r.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      className="text-left flex-1 min-w-0 hover:opacity-80"
                      onClick={() => startEdit(r)}
                    >
                      <span className="font-medium">{channelLabel(r.channel, t)}</span>
                      <span className="text-muted-foreground">
                        {" "}
                        — {t("app.reminderRules.ruleSummary", { hours: r.hours_before })}
                      </span>
                      {!r.enabled && (
                        <span className="ml-2 text-xs text-amber-600">{t("app.reminderRules.paused")}</span>
                      )}
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch checked={!!r.enabled} onCheckedChange={() => toggleRule(r)} />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("app.reminderRules.deleteTitle")}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t("app.reminderRules.deleteBody")}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("app.reminderRules.cancelEdit")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteRule(r.id)}>
                              {t("app.reminderRules.confirmDelete")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              {!loading && !rules.length && (
                <li className="px-4 py-8 text-center text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  {t("app.reminderRules.empty")}
                </li>
              )}
            </ul>

            <section>
              <h3 className="text-sm font-medium mb-2">{t("app.reminderRules.historyTitle")}</h3>
              <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm max-h-[360px] overflow-auto">
                {history.map((h) => (
                  <li key={h.id} className="px-4 py-3">
                    <span className="text-xs text-muted-foreground uppercase">{h.channel}</span>
                    <p className="font-medium mt-0.5">{h.client_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("app.reminderRules.historyLine", {
                        type: h.reminder_type,
                        appt: formatDate(h.appointment_start),
                        sent: formatDate(h.sent_at),
                      })}
                    </p>
                  </li>
                ))}
                {!history.length && (
                  <li className="px-4 py-8 text-center text-muted-foreground">{t("app.reminderRules.historyEmpty")}</li>
                )}
              </ul>
            </section>
          </div>
        </div>
      </AppLayout>
    </FeatureGate>
  )
}